/**
 * SalespersonSalaries
 *
 * Блок на главной: зарплаты продажников за выбранный месяц.
 *
 * Бэк (`/api/sales/analytics/salesperson-salaries?month=YYYY-MM`, только
 * admin) считает:
 *   - продажники — все сотрудники с ролью sales, у кого за месяц были оплаты;
 *   - зарплата = фикса + доход × процент (оба задаются в «Команде»);
 *   - доход — сумма книжных оплат, сведённая к KZT по фикс-курсам;
 *   - постоплата засчитывается ОРИГИНАЛЬНОМУ продавцу клиента (а не сборщику);
 *   - клиенты со статусом «Возврат»/«Архивировано» исключены.
 */
import React, { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { useInHouse } from "../context/inHousePermissions";
import "./SalespersonSalaries.css";

const MONTH_NAMES = [
  "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
  "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек",
];

interface SalespersonSalary {
  name: string;
  percentage: number;
  fixed: number;
  commission: number;
  salary: number;
  revenueKzt: number;
  revenueByCurrency: Record<string, number>;
  salesCount: number;
  prepaymentCount: number;
  fullPaymentCount: number;
  postpaymentCount: number;
}

interface ApiResponse {
  month: string;
  salespeople: SalespersonSalary[];
}

const fmtKzt = (v: number) => Math.round(v).toLocaleString("ru-RU") + "₸";
const fmtNum = (v: number) => v.toLocaleString("ru-RU");

// "1 200 000 KZT · 500 USD" — оригинальные суммы по валютам, KZT первым.
const formatByCurrency = (m: Record<string, number>) => {
  const entries = Object.entries(m).filter(([, v]) => v !== 0);
  if (entries.length === 0) return "—";
  entries.sort((a, b) => {
    if (a[0] === "KZT") return -1;
    if (b[0] === "KZT") return 1;
    return b[1] - a[1];
  });
  return entries.map(([cur, v]) => `${fmtNum(v)} ${cur}`).join(" · ");
};

const SalespersonSalaries: React.FC = () => {
  // Фикса и проценты — только для admin (бэк остальным отвечает 403).
  const { currentUserRecord } = useInHouse();
  const isAdmin = currentUserRecord?.role === "admin";

  // Месяц для выборки. Дефолт — текущий.
  const [selected, setSelected] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // Кнопки месяцев — последние 12.
  const monthButtons = useMemo(() => {
    const result: { year: number; month: number; label: string }[] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      result.push({
        year: d.getFullYear(),
        month: d.getMonth(),
        label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return result;
  }, []);

  const monthParam = `${selected.year}-${String(selected.month + 1).padStart(2, "0")}`;

  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: json } = await client.get<ApiResponse>(
          "/sales/analytics/salesperson-salaries",
          { params: { month: monthParam } },
        );
        if (alive) setData(json);
      } catch (err: any) {
        console.error("[salesperson-salaries] fetch failed", err);
        if (alive) setError(err?.message || "Ошибка загрузки");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [monthParam, isAdmin]);

  const totalSalary = useMemo(
    () => data?.salespeople.reduce((s, p) => s + p.salary, 0) ?? 0,
    [data],
  );

  if (!isAdmin) return null;

  return (
    <div className="sps">
      <div className="sps__header">
        <h2 className="sps__title">Зарплаты продажников</h2>
        <div className="sps__months">
          {monthButtons.map((mb) => {
            const active = mb.year === selected.year && mb.month === selected.month;
            return (
              <button
                key={`${mb.year}-${mb.month}`}
                className={"sps__month-btn" + (active ? " sps__month-btn--active" : "")}
                onClick={() => setSelected({ year: mb.year, month: mb.month })}
                type="button"
              >
                {mb.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="sps__error">{error}</div>}
      {loading && <div className="sps__hint">Загрузка…</div>}

      {data && (
        <div className="sps__card">
          <div className="sps__card-head">
            <span>К выплате (фикса + комиссия, в KZT)</span>
            <strong>{fmtKzt(totalSalary)}</strong>
          </div>
          <table className="sps__table">
            <thead>
              <tr>
                <th>Продажник</th>
                <th className="sps__num">Зарплата</th>
                <th className="sps__num">Фикса</th>
                <th className="sps__num">Комиссия</th>
                <th className="sps__num">Доход (KZT)</th>
                <th className="sps__num">Продаж</th>
                <th className="sps__num">Постоплат</th>
                <th>Доход по валютам</th>
              </tr>
            </thead>
            <tbody>
              {data.salespeople.map((p) => (
                <tr key={p.name}>
                  <td>
                    {p.name}{" "}
                    <span className="sps__pct">
                      {(p.percentage * 100).toLocaleString("ru-RU")}%
                    </span>
                  </td>
                  <td className="sps__num sps__salary">{fmtKzt(p.salary)}</td>
                  <td className="sps__num">{fmtKzt(p.fixed)}</td>
                  <td className="sps__num">{fmtKzt(p.commission)}</td>
                  <td className="sps__num">{fmtKzt(p.revenueKzt)}</td>
                  <td className="sps__num">
                    {fmtNum(p.salesCount)}
                    <span className="sps__sub">
                      {" "}
                      ({fmtNum(p.prepaymentCount)} пред / {fmtNum(p.fullPaymentCount)} полн)
                    </span>
                  </td>
                  <td className="sps__num">{fmtNum(p.postpaymentCount)}</td>
                  <td className="sps__cur">{formatByCurrency(p.revenueByCurrency)}</td>
                </tr>
              ))}
              {data.salespeople.length === 0 && (
                <tr>
                  <td colSpan={8} className="sps__hint">
                    За этот месяц продаж не было
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SalespersonSalaries;
